import type { FC } from 'react';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { formatDollars, formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import type { AltDealStructureGridCell } from '@makemydeal/dr-platform-types';
import {
    AffordabilityText,
    BottomRow,
    CellContainer,
    LenderText,
    MonthlyPaymentText,
    ProfitBadge,
    RateText,
    TopGroup,
    TopRow
} from './AltDealCellContent.styles';

interface AltDealCellContentProps {
    cell: AltDealStructureGridCell;
    dealType: AltDealStructureGridCell['dealType'];
}

const formatProfit = (profitTotal: number): string => {
    const sign = profitTotal >= 0 ? '+' : '-';
    return `${sign}${formatDollars(Math.abs(profitTotal))} profit`;
};

const formatAffordability = (affordabilityIndex: number): string => `Afford ${Math.round(affordabilityIndex * 100)}%`;

const formatRate = (cell: AltDealStructureGridCell, dealType: AltDealStructureGridCell['dealType']): string => {
    if (dealType === 'lease') {
        return cell.moneyFactor !== undefined ? `MF ${cell.moneyFactor.toFixed(3)}` : '';
    }
    return cell.apr !== undefined ? `${cell.apr.toFixed(2)}%` : '';
};

export const AltDealCellContent: FC<AltDealCellContentProps> = ({ cell, dealType }) => {
    const theme = useInterstateTheme();
    const isLease = dealType === 'lease';
    const showProfit = cell.profitTotal !== undefined;
    const rateText = formatRate(cell, dealType);

    return (
        <CellContainer $isLease={isLease} data-testid={`alt-deal-cell-content-${cell.id}`}>
            <TopGroup>
                {/* Profit shows for finance and lease; affordability stays finance-only.
                    Finance always renders the top row; lease renders it only when there's a profit to show. */}
                {(showProfit || !isLease) && (
                    <TopRow>
                        {showProfit ? (
                            <ProfitBadge theme={theme} variant="body-sm" data-testid={`alt-deal-cell-profit-${cell.id}`}>
                                {formatProfit(cell.profitTotal as number)}
                            </ProfitBadge>
                        ) : (
                            <span />
                        )}
                        {/* Affordability ("Afford %") is finance-only — no lease data source. */}
                        {!isLease &&
                            (cell.affordabilityIndex !== undefined ? (
                                <AffordabilityText
                                    theme={theme}
                                    variant="body-sm"
                                    data-testid={`alt-deal-cell-affordability-${cell.id}`}
                                >
                                    {formatAffordability(cell.affordabilityIndex)}
                                </AffordabilityText>
                            ) : (
                                <span />
                            ))}
                    </TopRow>
                )}
                <MonthlyPaymentText theme={theme} variant="h4" data-testid={`alt-deal-cell-payment-${cell.id}`}>
                    {formatDollarsAndCents(cell.monthlyPayment)}
                </MonthlyPaymentText>
            </TopGroup>
            <BottomRow theme={theme} $isLease={isLease}>
                {/* Lease shows the lender only when available (e.g. UPQ-sourced cells carry none). */}
                {cell.lender ? (
                    <LenderText theme={theme} variant="body-xs" data-testid={`alt-deal-cell-lender-${cell.id}`}>
                        {cell.lender}
                    </LenderText>
                ) : (
                    <span />
                )}
                {rateText && (
                    <RateText theme={theme} variant="body-xs" data-testid={`alt-deal-cell-rate-${cell.id}`}>
                        {rateText}
                    </RateText>
                )}
            </BottomRow>
        </CellContainer>
    );
};
